import {
  BankFileConversionRail,
  BodyText,
  Box,
  Card,
  FileConversionConfiguration,
  FileConversionService,
  SupportedCurrencies,
  useForm,
  withStyles,
} from 'vopay';
import { CircleButton, Button } from 'UI';
import { Dialog } from '@/providers/Dialog.provider';
import styles from './ProcessFileDialog.module.scss';
import { useTranslation } from 'react-i18next';
import processFile from '../svgs/process_file.svg';
import { useprocessFileFormFields, type ProcessFileFormValues } from './useProcessFileFormFields';
import useFileConversion from '@/hooks/useApi/useFileConversion';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';

export type ProcessFileSuccessResult = {
  transferType: string;
  paymentRail: '' | BankFileConversionRail;
  alternatePaymentRail?: '' | BankFileConversionRail;
  processor: string;
  numberOfTransactions: number;
  currency: SupportedCurrencies | '';
  totalAmount: string;
  totalFailed: number;
};

type ProcessFileDialogProps = {
  onCancel: () => void;
  onConfirm: (result: ProcessFileSuccessResult) => void;
  fileConversionID: string;
  configurations: FileConversionConfiguration[];
  configurationsLoading: boolean;
  service: FileConversionService;
};

const cn = withStyles(styles);

export type ProcessFileImgs = 'domainSetup';

export default function ProcessFileDialog({
  onCancel,
  onConfirm,
  fileConversionID,
  configurations,
  configurationsLoading,
  service,
}: ProcessFileDialogProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'FileUpload.History.FileConversionTable.ProcessFileDialog',
  });

  const { convertTo } = useprocessFileFormFields({ configurations, configurationsLoading });
  const alertDialog = useAlertDialog();
  const { submitFile } = useFileConversion();
  const [, setLoading] = useDataFetchIndicator();

  const getDefaultProcessorId = () => {
    if (configurations.length === 1 && configurations[0].Processor === 'default_processor') {
      return configurations[0].FileConversionConfigurationID;
    }

    return '';
  };

  const initialValues: ProcessFileFormValues = {
    Service: service,

    PaymentRail: '',
    FileConversionConfigurationID: getDefaultProcessorId(),
    ValueDate: new Date().getTime().toString(),
    AlternatePaymentRail: '',
  };

  const { renderFormElements, ctx } = useForm<ProcessFileFormValues>({
    initialValues,
    config: convertTo,
    onSubmit: async (values, ctx) => {
      try {
        setLoading(true);

        if (!values.PaymentRail) {
          alertDialog.error(
            t('processErrorTitle', { defaultValue: 'Error Processing File' }),
            t('paymentRailRequired', { defaultValue: 'Payment Rail is required.' }),
          );

          return;
        }

        const res = await submitFile({
          FileConversionID: fileConversionID,
          ValueDate: values.ValueDate,
          PaymentRail: values.PaymentRail,
          AlternatePaymentRail: values.AlternatePaymentRail === '' ? undefined : values.AlternatePaymentRail,
          FileConversionConfigurationID: ctx.values.FileConversionConfigurationID,
        });

        const config = configurations.find(
          (config) => config.FileConversionConfigurationID === values.FileConversionConfigurationID,
        );

        onConfirm({
          transferType: ctx.values.Service,
          paymentRail: values.PaymentRail,
          alternatePaymentRail: values.AlternatePaymentRail || undefined,
          processor: config?.FinancialInstitution || '',
          numberOfTransactions: res.TotalTransactions,
          currency: res.Currency,
          totalAmount: res.TotalAmount,
          totalFailed: res.TotalFailed,
        });
      } catch (err) {
        alertDialog.error(
          t('processErrorTitle', { defaultValue: 'Submission Error' }),
          err instanceof Error
            ? err.message
            : t('processErrorDesc', { defaultValue: 'There was an error processing the file.' }),
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Card className={cn('modalContainer')}>
      <form onSubmit={ctx.handleSubmit}>
        <div className={cn('header')}>
          <img src={processFile} alt={'process file'} className={cn('icon')} />
          <Dialog.Title level="4">{t('title', { defaultValue: 'Process File' })}</Dialog.Title>

          <BodyText className={cn('description')}>
            {t('desc', {
              defaultValue: `This will initiate the transaction batch submission. Once confirmed, the file will be processed and can't be modified.`,
            })}
          </BodyText>
        </div>

        <div className={cn('closeButton')}>
          <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
        </div>

        <Box flx="col" gap="common">
          {renderFormElements({ config: convertTo })}

          <Button type="submit" className={cn('button')} colorScheme="primary">
            {t('process', { defaultValue: 'Process' })}
          </Button>
        </Box>
      </form>
    </Card>
  );
}
